import { useAppSelector } from '@core/hooks/redux-hooks';
import { Pwerm2InputYearDebtInstrumentCalculationResultsPeriodTotalDto } from '@app/shared/models/contracts/pwerm2-input-calculation-results-dto';
import {
  FIELD_NAME_CASH_PAY_PROPORTION,
  FIELD_NAME_CASH_PAY_INTEREST,
} from '../utils/slnDisplayValues';
import useDealThesisData from '../../../deal-thesis-utils/useDealThesisData';

export const useSlnAndPrefShareData = (caseId: string) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedInputs);
  const caseResults = results.cases[caseId];

  const {
    filteredHistoricEventData,
    getPwermForecastCount,
    getMaxColspan,
    showHistoricDataColumns,
  } = useDealThesisData(caseId);

  const caseForecasts = Object.entries(caseResults.forecastDates).slice(0, getPwermForecastCount());

  const instrumentPresentInData = (instrumentId: string): boolean =>
    Object.values(caseResults.forecastDates).some(
      (forecastDate) => instrumentId in forecastDate.shareholderDebtInstrumentsEvents
    ) ||
    filteredHistoricEventData.some(
      (historicDate) => instrumentId in historicDate.shareholderDebtInstrumentsEvents
    );

  const hasAnyDataToDisplay = (
    instrumentId: string,
    key: keyof Pwerm2InputYearDebtInstrumentCalculationResultsPeriodTotalDto
  ): boolean => {
    const allEvents = [
      ...(showHistoricDataColumns
        ? filteredHistoricEventData.map(({ historicEventsYear }) => ({ historicEventsYear }))
        : []),
      ...caseForecasts.map(([forecastDate]) => ({ forecastDate })),
    ];

    return allEvents.some((event) => {
      const dateKey = 'historicEventsYear' in event ? event.historicEventsYear : event.forecastDate;
      const relevantObj =
        'historicEventsYear' in event
          ? caseResults.historicDates.historicEventYears?.[dateKey]
          : event.forecastDate in caseResults.forecastDates
          ? caseResults.forecastDates[event.forecastDate]
          : caseResults.valuationDate;

      const value =
        relevantObj?.shareholderDebtInstrumentsEvents?.[instrumentId]?.periodTotal?.[key];

      return Boolean(value);
    });
  };

  const shouldDisplayRow = (
    instrumentId: string,
    key: keyof Pwerm2InputYearDebtInstrumentCalculationResultsPeriodTotalDto
  ): boolean => {
    if (key === FIELD_NAME_CASH_PAY_PROPORTION || key === FIELD_NAME_CASH_PAY_INTEREST) {
      return hasAnyDataToDisplay(instrumentId, key);
    }
    return true;
  };

  return {
    caseResults,
    caseForecasts,
    filteredHistoricEventData,
    showHistoricDataColumns,
    getMaxColspan,
    instrumentPresentInData,
    shouldDisplayRow,
  };
};
